import { getAntiForgeryToken, legacyAccountAPI } from '../apis/authApi/authAPI';
import type { UserInfoBindingModel, UserInfoViewModelApp } from '../apis/authApi/types';
import {
  LANGUAGE_CODE_MAPPER,
  mapFromPreferredLanguageToELanguages,
} from '../apis/userAPI/helpers';
import type {
  getUserPermissions,
  getUserSettings,
  updateUserSettings,
  UserSettingsPatchBody,
  getUserById,
  User,
  UserPermissions,
  UserPatchBody,
  updateUser,
} from './users';
import type { UserById } from '../apis/userAPI/types';
import { legacyUserApi } from '../apis/utils/core';
import type { UserCompatibleWithAccountInfo } from '../containers/Login/LoginSlice/types';
import type { SelectListUser } from '../containers/CreateMessage/createMessageSlice/types';
import { checkIsAvailable } from '../utils/helperFunctions';
import { legacyImageUrlsMapper, legacyPhoneNumbersMapper } from './legacyCommonMappers';

export async function getLegacyUserById(userId: number): ReturnType<typeof getUserById> {
  const legacyUser = await legacyUserApi.getSingle<UserById>(userId);
  if (!legacyUser) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }

  return {
    data: {
      status: 'success',
      data: convertLegacyUserByIdToCore(legacyUser),
    },
    response: {} as Response,
  };
}

export const updateLegacyUser = async (
  body: UserPatchBody,
  primaryPhoneNumber?: string
): ReturnType<typeof updateUser> => {
  const csrfToken = await getAntiForgeryToken();
  const model: UserInfoBindingModel = {};
  if (body.phoneNumbers !== undefined) {
    model.phoneNumber = `${primaryPhoneNumber ?? ''}, ${body.phoneNumbers.secondary ?? ''}`;
  }
  if (body.unavailableUntil !== undefined) {
    model.unavailableDateTo = body.unavailableUntil;
    model.unavailable = body.unavailableUntil !== null;
  }
  const res = legacyAccountAPI.performExtra<UserById>({
    method: 'POST',
    model,
    extraResource: '',
    headers: { 'X-XSRF-Token': csrfToken },
  });
  if (!res) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }

  return {
    response: {} as Response,
    data: undefined,
    error: undefined,
  };
};

type BadRequest = {
  error: {
    status: 'error';
    error: {
      httpStatus: 400;
      code: 'BAD_REQUEST';
      details: {
        issues: {
          paramsResult: null;
          headersResult: null;
          queryResult: null;
          bodyResult: null;
        };
      };
    };
  };
  response: Response;
};

export async function getLegacyAccountInfo(): Promise<UserInfoViewModelApp | BadRequest> {
  const res = await legacyAccountAPI.performExtra<UserInfoViewModelApp>({
    method: 'GET',
    extraResource: 'info/app',
  });
  if (!res) {
    return {
      error: {
        status: 'error',
        error: {
          httpStatus: 400,
          code: 'BAD_REQUEST',
          details: {
            issues: {
              paramsResult: null,
              headersResult: null,
              queryResult: null,
              bodyResult: null,
            },
          },
        },
      },
      response: {} as Response,
    };
  }
  return res;
}

export async function getLegacyUserSettings(): ReturnType<typeof getUserSettings> {
  const res = await getLegacyAccountInfo();
  if ('error' in res) {
    return res;
  }

  return {
    data: {
      status: 'success',
      data: {
        receiveEmails: res.notifyWithEmail,
        notifications: {
          checklists: res.notifyChecklists,
          documents: res.notifyDocuments,
        },
        preferredLanguage: res.preferredLanguage
          ? (LANGUAGE_CODE_MAPPER[res.preferredLanguage] ?? null)
          : null,
      },
    },
    response: {} as Response,
  };
}

export const updateLegacyUserSettings = async (
  body: UserSettingsPatchBody
): ReturnType<typeof updateUserSettings> => {
  const csrfToken = await getAntiForgeryToken();
  if (body.preferredLanguage !== undefined) {
    const language = body.preferredLanguage
      ? mapFromPreferredLanguageToELanguages(body.preferredLanguage)
      : null;
    if (!language) {
      throw new Error('Language missing when updating user settings');
    }
    const res = legacyAccountAPI.performExtra({
      method: 'POST',
      extraResource: 'language',
      model: {
        language,
      },
      headers: { 'X-XSRF-Token': csrfToken },
    });
    if (!res) {
      return {
        error: {
          status: 'error',
          error: {
            httpStatus: 400,
            code: 'BAD_REQUEST',
            details: {
              issues: {
                paramsResult: null,
                headersResult: null,
                queryResult: null,
                bodyResult: null,
              },
            },
          },
        },
        response: {} as Response,
      };
    }
  }

  if (
    body.receiveEmails !== undefined ||
    body.notifications?.checklists !== undefined ||
    body.notifications?.documents !== undefined
  ) {
    const res = await legacyAccountAPI.performExtra({
      method: 'POST',
      extraResource: '',
      model: {
        notifyWithEmail: body.receiveEmails,
        notifyChecklists: body.notifications?.checklists,
        notifyDocuments: body.notifications?.documents,
      },
      headers: { 'X-XSRF-Token': csrfToken },
    });
    if (!res) {
      return {
        error: {
          status: 'error',
          error: {
            httpStatus: 400,
            code: 'BAD_REQUEST',
            details: {
              issues: {
                paramsResult: null,
                headersResult: null,
                queryResult: null,
                bodyResult: null,
              },
            },
          },
        },
        response: {} as Response,
      };
    }
  }

  return {
    response: {} as Response,
    data: undefined,
    error: undefined,
  };
};

export const getLegacyUserPermissions = async (): ReturnType<typeof getUserPermissions> => {
  const res = await getLegacyAccountInfo();
  if ('error' in res) {
    return res;
  }

  return {
    data: {
      status: 'success',
      data: mapLegacyUserToUserPermissions(res),
    },
    response: {} as Response,
  };
};

export const convertLegacyUserByIdToCore = (legacyUser: UserById): User => ({
  id: legacyUser.id,
  name: legacyUser.displayName,
  email: legacyUser.email,
  title: legacyUser.title,
  imageUrls: legacyImageUrlsMapper.fromLegacyToCore(legacyUser.photoFileName),
  phoneNumbers: legacyPhoneNumbersMapper.fromLegacyToCore(legacyUser.phoneNumber),
  lastAccessAt: legacyUser.lastAccess,
  unavailableUntil: legacyUser.unavailableDateTo,
});

export const convertLegacyAccountInfoToCore = (
  legacyAccount: UserInfoViewModelApp
): UserCompatibleWithAccountInfo => ({
  id: legacyAccount.id,
  name: legacyAccount.name,
  email: legacyAccount.email,
  imageUrls: legacyImageUrlsMapper.fromLegacyToCore(legacyAccount.photoFileName),
  unavailableUntil: legacyAccount.unavailableDateTo,
});

export const convertUserToLegacyListUser = (
  user: UserCompatibleWithAccountInfo
): SelectListUser => {
  const isAvailable = checkIsAvailable(user.unavailableUntil);
  return {
    id: user.id,
    displayName: user.name,
    email: user.email,
    title: user.title ?? null,
    photoFileName: legacyImageUrlsMapper.fromCoreToLegacy(user.imageUrls),
    available: isAvailable,
    unavailable: !isAvailable,
    unavailableDateTo: user.unavailableUntil,
    creatorId: null,
  };
};

const mapLegacyUserToUserPermissions = (user: UserInfoViewModelApp): UserPermissions => {
  const roleSet = new Set(user.roles ?? []);

  const menuItemNames = user.menuItems?.map(item => item.technicalName) ?? [];
  const orderedFunctions = [
    ...menuItemNames.filter(item => item !== 'alarm'),
    ...(roleSet.has('Informationsutskick') ? ['broadcast'] : []),
    ...(roleSet.has('HoldingStatement') ? ['holdingStatement'] : []),
    ...(roleSet.has('LogNotes') ? ['logNotes'] : []),
    ...(roleSet.has('ExternalContacts') ? ['cec'] : []),
    ...(menuItemNames.includes('alarm') ? ['alarm'] : []),
  ];
  const menuItemMap = new Map();
  orderedFunctions.forEach((menuItem, index) => {
    menuItemMap.set(menuItem, { displayOrder: index + 1 });
  });

  const hasChecklistRole = roleSet.has('EditLiveChecklists');
  const hasImOkRole = roleSet.has('ManageMuster');
  const hasOnCallAlertRole = roleSet.has('OnCallAlerts');
  const hasDocumentsRole = roleSet.has('DocumentsManager');

  return {
    alarms: {
      mayActivate: menuItemMap.has('alarm'),
      mayDeactivate: roleSet.has('EndAlarm'),
      displayOrder: menuItemMap.get('alarm')?.displayOrder ?? null,
    },
    broadcast: {
      maySend: menuItemMap.has('broadcast'),
      displayOrder: menuItemMap.get('broadcast')?.displayOrder ?? null,
    },
    checklists: {
      mayUse: menuItemMap.has('checklists'),
      mayCreateTemplate: hasChecklistRole,
      mayAddTask: hasChecklistRole,
      mayEditAccess: hasChecklistRole,
      mayRename: hasChecklistRole,
      mayActivate: menuItemMap.has('checklists'),
      mayDeactivate: hasChecklistRole,
      mayReactivate: hasChecklistRole,
      displayOrder: menuItemMap.get('checklists')?.displayOrder ?? null,
    },
    cec: {
      maySend: menuItemMap.has('cec'),
      displayOrder: menuItemMap.get('cec')?.displayOrder ?? null,
    },
    documents: {
      mayRead: menuItemMap.has('documents'),
      mayAdd: hasDocumentsRole,
      mayManageOffline: hasDocumentsRole,
      mayEditAccess: hasDocumentsRole,
      mayRename: hasDocumentsRole,
      mayDelete: hasDocumentsRole,
      displayOrder: menuItemMap.get('documents')?.displayOrder ?? null,
    },
    groups: {
      mayInteractWithOwn: menuItemMap.has('groups'),
      mayInteractWithAll: roleSet.has('SeeOrgGroups'),
      displayOrder: menuItemMap.get('groups')?.displayOrder ?? null,
    },
    holdingStatement: {
      mayCreate: menuItemMap.has('holdingStatement'),
      displayOrder: menuItemMap.get('holdingStatement')?.displayOrder ?? null,
    },
    imOk: {
      mayReceive: menuItemMap.has('muster'),
      mayActivate: hasImOkRole,
      mayDeactivate: hasImOkRole,
      mayMessage: hasImOkRole,
      mayEdit: hasImOkRole,
      mayDelete: hasImOkRole,
      displayOrder: menuItemMap.get('muster')?.displayOrder ?? null,
    },
    logNotes: {
      mayCreate: menuItemMap.has('logNotes'),
      displayOrder: menuItemMap.get('logNotes')?.displayOrder ?? null,
    },
    loneWorkerTimer: {
      mayActivate: menuItemMap.has('group_alarm_work_alone'),
      displayOrder: menuItemMap.get('group_alarm_work_alone')?.displayOrder ?? null,
    },
    meetingRoom: {
      mayJoin: menuItemMap.has('meeting_room'),
      displayOrder: menuItemMap.get('meeting_room')?.displayOrder ?? null,
    },
    messages: {
      mayCreate: menuItemMap.has('messages'),
      mayAddRecipients: roleSet.has('AddRecipientsToAnyMessage'),
      mayBlock: roleSet.has('ChangeMessageType'),
      mayDelete: roleSet.has('DeleteMessage'),
      displayOrder: menuItemMap.get('messages')?.displayOrder ?? null,
    },
    news: {
      mayRead: menuItemMap.has('news'),
      displayOrder: menuItemMap.get('news')?.displayOrder ?? null,
    },
    onCallAlert: {
      mayReceive: menuItemMap.has('oncall_alert'),
      mayActivate: hasOnCallAlertRole,
      mayDeactivate: hasOnCallAlertRole,
      mayMessage: hasOnCallAlertRole,
      mayEdit: hasOnCallAlertRole,
      mayDelete: hasOnCallAlertRole,
      displayOrder: menuItemMap.get('oncall_alert')?.displayOrder ?? null,
    },
    panicButton: {
      mayActivate: menuItemMap.has('group_alarm'),
      displayOrder: menuItemMap.get('group_alarm')?.displayOrder ?? null,
    },
    www: {
      maySee: menuItemMap.has('organization_website'),
      displayOrder: menuItemMap.get('organization_website')?.displayOrder ?? null,
    },
  };
};
